<template>
	<div>
		<v-row id="form_input_field" no-gutters class="text-left fif px-0 pt-2">
			<v-col cols="12" class="text-left" style="position: relative">
				<div v-if="hasExternalSourceAndNotTableRelated">
					<v-text-field
						v-if="tabIsExternalSource"
						:value="localValueDisplay"
						outlined
						color="#5C8DF5"
						height="52"
						class="param_field mb-4"
						@focus="inputFocus"
						@click="inputClick"
						@blur="inputBlur"
						:placeholder="param.name"
						:loading="loading"
						:rules="fieldRules"
						hide-details
						readonly
						ref="external"
					>
						<template v-slot:append>
							<v-btn
								v-if="localValueDisplay && localValueDisplay.includes(USER_MATCHING_TEXT)"
								text
								outlined
								class="ml-3 options-btn"
								width="105"
								height="24"
								color="primary"
								style="right: 0px; top: 0px"
								@click="openUserMatchingFallback = true"
							>
								Edit options
							</v-btn>
							<v-btn class="clear-btn" @click="clearValue">
								<v-icon class="clear-btn-icon"> mdi-close-circle </v-icon>
							</v-btn>
						</template>
					</v-text-field>
					<div v-else>
						<RichTextBox
							ref="external"
							:isFocused="showDataMenu"
							:showDataMenu="showDataMenu"
							@focus="inputFocus"
							@click="inputClick(true)"
							@blur="inputBlur"
							v-model="localValue"
							:textAreaMode="isTextArea"
							:inputFieldHint="inputFieldHint"
							:multipleSelection="multipleSelection"
							@update-local-value="updateLocalValue"
							v-on="$listeners"
						/>
						<div class="suffix-icons" style="margin-top: -2px">
							<DropdownArrowSvg :class="['dropdown-icon', { 'dropdown-open': showDataMenu }]" />
						</div>
					</div>
				</div>

				<v-select
					v-else-if="isInputFieldTypeSelect"
					v-model="localValue"
					:items="selectItems"
					:placeholder="inputFieldPlaceholder"
					:hint="inputFieldHint"
					:persistent-hint="inputFieldHint ? true : false"
					:rules="fieldRules"
					:clearable="!isRequired"
					:open-on-clear="true"
					@click:clear="clearSelect"
					hide-details
					:readonly="readonly"
				>
				</v-select>

				<div class="com_wrp" v-else>
					<RichTextBox
						ref="reg"
						class="mb-4"
						:isFocused="isFocused"
						:showDataMenu="showDataMenu"
						@focus="inputFocus"
						@click="inputClick"
						@blur="inputBlur"
						v-model="localValue"
						:textAreaMode="isTextArea"
						:inputFieldHint="inputFieldHint"
						:multipleSelection="multipleSelection"
						@update-local-value="updateLocalValue"
						:readonly="readonly"
					/>
					<div v-if="isNoValue && showOpenMenuButton" class="prefix-icons">Type '/' to insert data</div>
					<div v-if="showOpenMenuButton && !readonly" class="suffix-icons">
						<OpenMenuButton @open="openMenu" />
					</div>
				</div>

				<transition name="fromBottomMenu" mode="out-in">
					<div
						class="insert-data-container"
						style="position: absolute"
						v-click-outside="{ handler: onClickOutside, include: getAbsoluteMenuElements }"
						v-if="needDataInputMenu"
					>
						<DataInputMenuAbsoluteMenuWrapper v-model="showDataMenu" v-bind="absoluteMenuProps">
							<DataInputMenu
								:localValue="localValue"
								:search.sync="search"
								:tab.sync="tab"
								:param="param"
								:fieldParams="items"
								:allowMultipleSelection="allowMultipleSelection"
								:nodeId="nodeID"
								:tabItems="tabItems"
								:curTabCode="curTabCode"
								:branchID="branchID"
								@refresh-data="refreshData"
								@update-external-localValue="updateExternalLocalValue"
								@update-previous-step-localValue="updateValueFromMenu"
								@update-text-localValue="updateValueFromMenu"
								:hasMoreData="hasMoreData"
								:loading="loading"
								@close="showDataMenu = false"
								:showMenuFlag="showDataMenu"
								:valuesTypes="valuesTypes"
								ref="data_input_menu"
								@load-more="() => endIntersect(null, null, true)"
								:didOpenBySlash="didOpenBySlash"
							>
							</DataInputMenu>
						</DataInputMenuAbsoluteMenuWrapper>
					</div>
				</transition>
			</v-col>
		</v-row>
		<div>
			<UserMatchingFallbackModal
				:showDialog.sync="openUserMatchingFallback"
				@update-params-fallback="updateFallback"
				:isRequired="isRequired"
				:appInfo="nodeService || {}"
				:paramName="inputFieldName"
				:actionName="nodeAction"
				:chooseFromItems="originalItems"
				:fallbackInit="userMatchingFallback"
			/>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import OpenMenuButton from '@/components/BlockEditor/BlockEditorParams/inputsComponents/assets/OpenMenuButton';

import api from '@/api';
import pagination from '@/api/pagination';
import { rootNode } from '@/store/modules/selectedWorkflow/getters';
import { getAction } from '@/store/modules/actions/getters';
import { getService } from '@/store/modules/apps/getters';
import AppService from '@/utils/AppService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';

import RichTextBox from '@/components/common/RichTextBox/RichTextBox';
import DataInputMenu from '@/components/DataInput/DataInputMenu/DataInputMenu';
import UserMatchingFallbackModal from './UserMatchingFallbackModal';

import { isNullOrUndefined, isNullOrUndefinedOrEmpty, formatArray } from '@/utils/helpers';
import UserMatchingService from '@/utils/UserMatchingService';
import isObjectLike from 'lodash/isObjectLike';
import { PLY_PARAMS } from '@/utils/ParamsService';
import DropdownArrowSvg from '@/components/BlockEditor/BlockEditorParams/inputsComponents/assets/DropdownArrowSvg';

import { EXTERNAL_SOURCE_TAB_KEY, THIS_FLOW_TAB_KEY } from '../../DataInput/DataInputMenu/helpers/data-input-menu.const';
import DataInputMenuService from '@/components/DataInput/DataInputMenu/data-input-menu-service';
import DataInputMenuAbsoluteMenuWrapper from '@/components/common/DataInputMenuAbsoluteMenuWrapper';

// mixins that responsible on reposition DataInputMenu triggered by IntersectionObserver or scroll event
import IntersectionObserverMixin from '@/components/BlockEditor/BlockEditorParams/DataInputMenus/mixins/IntersectionObserverMixin';
import ScrollResizeListenersMixin from '@/components/BlockEditor/BlockEditorParams/DataInputMenus/mixins/ScrollResizeListenersMixin';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';

/**
 * this component is currently in use by BranchRuleParam (run specific branch),
 * ParameterBranchMapping (consolidate data of branch)
 * and ActionContextNodeParams (first node of flow with context)
 */
export default {
	name: 'ParamsFormInputField',
	mixins: [IntersectionObserverMixin, ScrollResizeListenersMixin],
	components: {
		DataInputMenu,
		RichTextBox,
		OpenMenuButton,
		UserMatchingFallbackModal,
		DropdownArrowSvg,
		DataInputMenuAbsoluteMenuWrapper,
	},
	props: {
		isOpen: { type: Boolean },
		param: { type: Object },
		node: { type: Object },
		initValues: { type: Object, required: false },
		multipleSelection: { type: Boolean, default: true },
		branchID: { type: [String, Number] },
		overrideRouteValidation: { type: Boolean, default: false },
		alternativeParamsMap: { type: Object },
		valuesTypes: { type: Array, validator: DataInputMenuService.valueTypesValidator, default: () => [] },
		readonly: { type: Boolean },
	},
	created() {
		window.addEventListener('keydown', (e) => {
			if (this.isFocused && e.key === '/') {
				this.didOpenBySlash = true;
				this.openMenu();
			}
		});
		const func = async function () {
			const nodeID = this.nodeID;
			if (parseInt(nodeID) !== parseInt(this.$route.params.nodeID) && !this.overrideRouteValidation) {
				return;
			}
			if (!this.hasExternalSource || !this.needGetOptionsForAction || this.cantGetOptionsForAction) {
				return;
			}
			this.loading = true;
			const actionSource = this.inputFieldSource.split('.')[0];
			const ret = await api.getOptionsForAction(nodeID, actionSource, this.search, this.inputFieldKey, this.page, this.retrievalId);

			this.loading = false;

			const response = pagination.consolidateGetOptionsForActionResponse(ret);
			if (response.errorMessage) {
				this.hasMoreData = false;
				return;
			}
			this.hasMoreData = response.hasMoreData;
			this.retrievalId = response.retrievalId;
			if (this.page === 1) {
				this.$set(this, 'originalItems', response.results);
			} else {
				this.originalItems.push(...response.results);
			}
		};
		this.getData = debounce(func.bind(this), 1100);
		this.getDataOnDemand = func.bind(this);
	},
	mounted() {
		if (this.hasExternalSource) {
			this.getDataOnDemand();
		}
		this.paramFallback = this.userMatchingFallback;
		this.setInitialValue();
		this.isMounted = true;
	},
	computed: {
		...mapGetters([
			getAction.fullPath,
			getService.fullPath,
			rootNode.fullPath,
			getConfigurationFieldResponse.fullPath,
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
			isConfigurationsOn.fullPath,
		]),
		tabItems() {
			const fieldName = this.param.name ?? 'fieldName';
			var arr = [
				{ tab: THIS_FLOW_TAB_KEY, title: 'This Flow ' },
				// { tab: 'triggeringFlows', title: 'Triggering Flows' },
				// { tab: 'configData', title: 'Config Data' },
			];
			if (this.hasExternalSource) {
				arr = [{ tab: EXTERNAL_SOURCE_TAB_KEY, title: fieldName }, ...arr];
			}
			return arr;
		},
		showOpenMenuButton() {
			return this.isFocused && !this.showDataMenu;
		},
		isNoValue() {
			return !this.initValues.value || this.initValues.value === '';
		},
		nodeID() {
			return this.node.id;
		},
		nodeUUID() {
			return this.node.uuid;
		},
		nodeService() {
			return this['apps/getService'](this.node.service);
		},
		nodeAction() {
			const action = this['actions/getAction'](this.node.action);
			return action?.title;
		},
		isTextArea() {
			return this.param.name.toLowerCase() === 'message';
		},
		selectItems() {
			if (this.param.hasOwnProperty('options')) {
				return this.param.options;
			}
			return [];
		},
		userMatchingFallback() {
			if (!this.node || !this.node.user_matching_fallbacks) {
				return undefined;
			}
			const found = this.node.user_matching_fallbacks.find((x) => x.param_key == this.inputFieldKey);
			if (found) {
				return {
					type: found.fallback_type,
					details: found.fallback_details,
				};
			}
			return undefined;
		},
		inputFieldName() {
			return this.param.name;
		},
		isRequired() {
			return this.param.required;
		},
		inputFieldKey() {
			return this.param.key;
		},
		inputFieldHint() {
			return this.param.placeholder;
		},
		inputFieldPlaceholder() {
			return this.param.name;
		},
		inputFieldType() {
			return this.param.type;
		},
		inputFieldSource() {
			return this.param.source ? this.param.source : '';
		},
		fieldRules() {
			const rules = {};
			if (this.isUserMatching) {
				rules['fallbackRequired'] = UserMatchingService.validFallback(this.paramFallback) || 'This field requiures fallback';
			}
			if (this.isRequired) {
				rules['required'] = (val) => (val || '').length > 0 || 'This field is required';
			}
			return Object.values(rules);
		},
		hasExternalSource() {
			return 'source' in this.param;
		},
		hasExternalSourceAndNotTableRelated() {
			return this.hasExternalSource && this.inputFieldType !== 'rules';
		},
		curTab() {
			return this.tabItems[this.tab];
		},
		curTabCode() {
			return this.curTab.tab;
		},
		USER_MATCHING_TEXT() {
			return UserMatchingService.USER_MATCHING_TEXT;
		},
		items() {
			if (this.param.user_matching_optional) {
				return [
					UserMatchingService.buildUserMatchingOption(this[rootNode.fullPath], this.nodeUUID, this.inputFieldKey),
					...this.originalItems,
				];
			}
			return this.originalItems;
		},
		rulesItems() {
			return this.items.map((item) => ({ key: item.option, example: isObjectLike(item.value) ? JSON.stringify(item.value) : item.value }));
		},
		allowMultipleSelection() {
			return this.param.multiple_select || this.param.type === 'optional_params_picker' || this.param.type === 'multiple_select';
		},
		externalSourceReturnObject() {
			return this.param.source_return_full_object;
		},
		needGetOptionsForAction() {
			const dependsOn = this.param.depends_on || [];
			let paramsMap = this.alternativeParamsMap || this.node.params_map;

			if (this.node.user_schema_mappings_source_field_uuid) {
				paramsMap = WorkflowNodeService.extendParamsMapWithParamsFromDynamicMapperIfExists(
					this.node,
					this[getConfigurationAndInterfaceIdByFieldUUID.fullPath],
					this[getConfigurationFieldResponse.fullPath],
					this[isConfigurationsOn.fullPath],
					paramsMap
				);
			}

			// all the params of depended keys are already set
			return dependsOn.every((paramKey) => {
				const paramValue = paramsMap[paramKey];
				return !isNullOrUndefinedOrEmpty(paramValue);
			});
		},
		cantGetOptionsForAction() {
			return (
				!this.nodeService ||
				(isNullOrUndefinedOrEmpty(this.node.credentials_configuration.credentials) && AppService.doesRequiresAuth(this.nodeService))
			);
		},
		isUserMatching() {
			return (
				(this.localValue && this.localValue.type && this.localValue.type === UserMatchingService.USER_MATCHING_TYPE) ||
				this.localValueDisplay == UserMatchingService.USER_MATCHING_TEXT
			);
		},
		isTableDataInput() {
			return this.param.key === PLY_PARAMS.listSourceParam.key;
		},
		tabIsExternalSource() {
			return this.curTabCode === EXTERNAL_SOURCE_TAB_KEY;
		},
		absoluteMenuProps() {
			return {
				positionX: this.positionX,
				positionY: this.positionY,
				[this.menuDirection]: true,
				'close-on-content-click': false,
				'close-on-click': false,
			};
		},
		needDataInputMenu() {
			return !this.isInputFieldTypeSelect;
		},
		isInputFieldTypeSelect() {
			return this.inputFieldType === 'select';
		},
	},
	watch: {
		showDataMenu(n) {
			this.$emit('data-menu-status', { param: this.param, opened: n });
			if (n) {
				// IntersectionObserver logic - managed in IntersectionObserverMixin
				this._createIntersectionObserver(this._handleIntersectionEntries);
				this.$data._intersectionObserver.observe(this.getRichTextBoxElement());

				this._listenToEvents({ targetElement: this.getRichTextBoxElement() });
			} else {
				// IntersectionObserver logic - managed in IntersectionObserverMixin
				this._removeIntersectionObserver();

				this._removeEventListeners();
				if (this.didOpenBySlash) {
					this.getRichTextBox()?.focus();
				}
				this.didOpenBySlash = false;
			}
		},
		search: {
			handler() {
				this.refreshData();
			},
		},
		showMenu: {
			handler(newV) {
				if (newV) {
					if (this.hasExternalSource && this.tabIsExternalSource) {
						this.refreshData();
					}
				} else {
					this.$emit('update:dataMenu', false);
				}
			},
		},
		localValue: {
			handler(newV) {
				this.$emit('update-input-field', {
					map: { key: this.inputFieldKey, value: newV },
					// label: { key: this.inputFieldKey, value: this.localValueDisplay },
					param: this.param,
				});
				this.$emit('update-input-field-label', {
					label: { key: this.inputFieldKey, value: this.tabIsExternalSource ? this.localValueDisplay : '' },
					param: this.param,
				});
			},
			deep: true,
		},
		cantGetOptionsForAction: {
			handler(newV, oldV) {
				if (!newV && oldV) {
					this.getData();
				}
			},
		},
		initValues: {
			handler() {
				this.setInitialValue();
			},
		},
	},
	methods: {
		openMenu() {
			this.calcAbsoluteMenuPosition();
			this.showDataMenu = true;
			this.$emit('update:dataMenu', true);
		},
		onClickOutside() {
			if (!this.isFocused) {
				this.showDataMenu = false;
			}
		},
		updateLocalValue(newVal) {
			if (this.tabIsExternalSource) return;
			this.localValueDisplay = '';
			this.localValue = newVal;
		},
		getParamForNode(paramKey) {
			if (!this.node.hasOwnProperty('params_map') || !this.node.params_map.hasOwnProperty(paramKey)) {
				return null;
			}
			return this.node.params_map[paramKey];
		},
		getParamLabelForNode(paramKey) {
			if (!this.node.hasOwnProperty('params_map_labels') || !this.node.params_map_labels.hasOwnProperty(paramKey)) {
				return null;
			}
			return this.node.params_map_labels[paramKey];
		},
		updateExternalLocalValue(payload) {
			// console.log(`updateExternalLocalValue:`, payload);
			const payloadIsArray = Array.isArray(payload);
			if (payload.type && payload.type == UserMatchingService.USER_MATCHING_TYPE) {
				this.openUserMatchingFallback = true;
				this.paramFallback = !isNullOrUndefined(this.userMatchingFallback)
					? this.userMatchingFallback
					: UserMatchingService.initialFallback(this.isRequired);
				this.updateFallback(this.paramFallback);
			}
			// Legacy we had support for .id as well.
			// Generate the display for an Array
			if (Array.isArray(payload)) {
				this.localValueDisplay = formatArray(payload.map((entry) => entry.option));
				this.localValue = payload;
			} else {
				this.localValueDisplay = payload.name || payload.option;
				// Flag to support returning the whole object and not just value.
				if (this.externalSourceReturnObject) {
					this.localValue = payload;
				} else {
					this.localValue = payload.value;
				}
			}

			// Dismiss menu only if we are not allowing to pick more than one
			if (!payloadIsArray) {
				this.showDataMenu = false;
				this.inputBlur();
			}
		},
		updateValueFromMenu(value) {
			this.showMenu = false;
			if (!this.localValue || this.localValueDisplay) {
				this.localValue = value;
			} else {
				if (this.hasExternalSource) {
					this.$refs.external.addValue(`${value}`, this.didOpenBySlash);
				} else {
					this.$refs.reg.addValue(`${value}`, this.didOpenBySlash);
				}
			}
			this.localValueDisplay = '';
		},
		updateFallback(payload) {
			this.paramFallback = payload;
			const keyFallback = { key: this.inputFieldKey, value: payload };
			this.$emit('update-input-fallback', keyFallback);
		},

		setInitialValue() {
			let initValue;
			let initValueLabel;
			if (this.initValues) {
				initValue = this.initValues.value;
				initValueLabel = this.initValues.valueLabel;
			} else {
				initValue = this.getParamForNode(this.inputFieldKey);
				initValueLabel = this.getParamLabelForNode(this.inputFieldKey);
			}
			if (this.hasExternalSource && !initValueLabel && initValue) {
				this.tab = 1;
			}
			this.initRules = initValue ? initValue : null;
			this.localValue = initValue;
			this.localValueDisplay = initValueLabel;
		},
		inputFocus() {
			if (!this.isFocused) {
				this.isFocused = true;
			}
		},
		inputClick(openOnClick) {
			if (this.isNoValue || openOnClick) {
				this.openMenu();
			}
		},
		inputBlur() {
			if (this.isFocused) {
				this.isFocused = false;
			}
		},
		clearValue() {
			this.updateExternalLocalValue({ id: '', name: '' });
		},
		clearSelect() {},
		onRulesChange(newFilter) {
			this.localValue = newFilter;
		},
		seeMore() {
			this.page++;
			// this.hasMoreData = false;
			this.getDataOnDemand();
		},
		refreshData() {
			this.page = 1;
			this.hasMoreData = false;
			this.getDataOnDemand();
		},
		endIntersect(entries, observer, isIntersecting) {
			if (isIntersecting) {
				this.seeMore();
			}
		},
		getAbsoluteMenuElements() {
			return [this.getRichTextBoxElement(), ...DataInputMenuService.getDataInputMenuAbsoluteElements()];
		},
		calcAbsoluteMenuPosition(args) {
			let { targetElement } = args || {};

			if (!targetElement) {
				targetElement = this.getRichTextBoxElement();
			}
			const menuRect = DataInputMenuService.getMenuRect({ ...args, targetElement });
			this.menuDirection = menuRect.dir;
			this.positionX = menuRect.x;
			this.positionY = menuRect.y;
		},
		getRichTextBox() {
			return this.$refs.reg || this.$refs.external;
		},
		getRichTextBoxElement() {
			return this.getRichTextBox()?.$el;
		},
		_handleIntersectionEntries(entries) {
			entries.forEach(this._handleIntersectionEntry);
		},
		_handleIntersectionEntry(entry) {
			// observer is observing one element at a time, so if it's not intersecting with it, we should hide menu
			if (!entry.isIntersecting) {
				this.showDataMenu = false;
			}
		},
		/**
		 * onScroll handler
		 * @param {*} args
		 */
		_onScroll(args) {
			this.calcAbsoluteMenuPosition(args);
		},
		/**
		 * onResize handler
		 * @param {*} args
		 */
		_onResize(args) {
			this.calcAbsoluteMenuPosition(args);
		},
		_listenToEvents(args) {
			// sign for scroll and resize events - managed in ScrollResizeListenersMixin
			this._listenOnScroll(args, this._onScroll);
			this._listenOnResize(args, this._onResize);
		},
		_removeEventListeners() {
			this._removeOnScrollListener();
			this._removeOnResizeListener();
		},
	},
	data() {
		return {
			isMounted: false,
			rulesLoading: true,
			isFocused: false,
			showDataMenu: false,
			showMenu: false,
			code: '',
			combineValue: '',
			loading: false,
			originalItems: [],
			search: '',
			localValue: null,
			localValueDisplay: null,
			tab: 0,
			getDataOnDemand: null,
			openUserMatchingFallback: false,
			paramFallback: undefined,
			initRules: null,
			page: 1,
			hasMoreData: false,
			retrievalId: null,
			positionX: null,
			positionY: null,
			menuDirection: 'bottom',
			didOpenBySlash: false,
		};
	},
};
</script>

<style scoped lang="scss">
.com_wrp {
	position: relative;
	display: block;
	width: 100%;
	border-radius: 5px;
}
.options-btn {
	font-weight: 400 !important;
	font-size: 14px !important;
	border-radius: 2px !important;
	border-color: currentColor !important;
}
.clear-btn,
.clear-btn:focus,
.clear-btn:hover {
	width: 35px;
	height: 0 !important;
	top: 12px;

	.clear-btn-icon {
		right: 0px;
	}
}
.table-action-settings {
	position: relative;

	.field-settings {
		padding: 16px;
	}

	.source-selection {
		position: relative;

		.sub-field-container {
			margin: 6px 0 0 0;

			.sub-field {
				margin: 5px 0 0 0;
			}
			&.disabled {
				opacity: 0.5;
				pointer-events: none;

				&:hover {
					cursor: not-allowed;
				}
			}
		}
	}
}
.suffix-icons {
	right: 10px;
	position: absolute;
	top: 9px;
	z-index: 1;
}
.prefix-icons {
	left: 10px;
	position: absolute;
	top: 9px;
	font-size: 14px;
	color: var(--insert-data-placeholder);
	z-index: 1;
}
.dropdown-icon {
	transition: 0.2s transform;
	&.dropdown-open {
		transform: rotateX(180deg);
	}
}
</style>
